import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { useCallback } from "react";

import type { IApiResponse } from "@/api";
import { useUserStore } from "@/stores/userStore";
import { notify } from "@/components/atoms/notify";

export const useErrorHandler = (
  onThrow: (message: string, status: number) => void,
) => {
  const { t } = useTranslation("common");

  const onError = useCallback(
    (error: string) => {
      notify.error({ title: t("apiError.error"), text: error });
    },
    [t],
  );

  return useCallback(
    async (error: AxiosError<IApiResponse<unknown>>) => {
      if (!!error.response || error.code !== "ERR_CANCELED") {
        Sentry.setTags({
          errorCode: error?.code,
          httpStatusCode: error?.response?.status,
        });
        Sentry.captureException(error);
      }

      if (!error.response) {
        if (error.code !== "ERR_CANCELED") {
          // "ERR_CANCELED" happened if the file query or mutation has been canceled.
          // in this case we don't want to show an error message.
          onError(t("apiError.unable_server_response"));
        }
        return Promise.reject(error);
      }

      switch (error.response.status) {
        case 401:
          useUserStore.getState().logout();
          onError(t("apiError.401"));
          break;

        case 403:
          onError(t("apiError.403"));
          onThrow(t("apiError.403"), 403);
          break;
        case 500:
          onError(t("apiError.500"));
          onThrow(t("apiError.500"), 500);
          break;
        case 400:
          onError(t("apiError.400"));
          onThrow(t("apiError.400"), 400);
          break;
        case 503:
          onError(t("apiError.503"));
          onThrow(t("apiError.503"), 503);
          break;
        case 404:
          onError(t("apiError.404"));
          onThrow(t("apiError.404"), 404);
          break;

        case 406:
          // back-end guarantees this behavior.
          onError(error.response.data.message!);
          break;

        case 422:
          if (error.response.data.errors) {
            onError(Object.values(error.response.data.errors).join("\n"));
          }
          break;

        default:
          onError(error.response.data.message ?? t("apiError.error"));
      }

      return Promise.reject(error);
    },
    [onError, onThrow, t],
  );
};
